import React, { useEffect, useState, useRef } from "react";
import * as THREE from "three";
import "./ThreeScene.css";

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useLocation } from "react-router-dom";

const ThreeScene = () => {
  const mountRef = useRef(null);
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const mixerRef = useRef();
  const modelRef = useRef();
  const headUpdateFunctionRef = useRef(null);

  const location = useLocation();
  const [modelName, setModelName] = useState("idle_vahan.glb");

  function enableHeadLookAt(head, camera, renderer) {
    const mouse = new THREE.Vector2();
    const initialQuaternion = head.quaternion.clone();
    const targetQuaternion = new THREE.Quaternion();
    const rotationSpeed = 0.15;

    function onMouseMove(event) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Limits
      const maxRotationX = 0.3;
      const maxRotationY = 0.5;

      const quaternionX = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(1, 0, 0),
        -mouse.y * maxRotationX
      );

      const quaternionY = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 1, 0),
        mouse.x * maxRotationY
      );

      targetQuaternion.copy(initialQuaternion);
      targetQuaternion.multiply(quaternionY);
      targetQuaternion.multiply(quaternionX);
    }

    function update() {
      head.quaternion.slerp(targetQuaternion, rotationSpeed);
    }

    window.addEventListener("mousemove", onMouseMove);

    head.userData.update = update;
    head.userData.cleanup = () => {
      window.removeEventListener("mousemove", onMouseMove);
    };

    return update;
  }

  useEffect(() => {
    if (location.pathname === "/education") {
      setModelName("read_vahan.glb");
    } else if (location.pathname === "/experience") {
      setModelName("working_vahan.glb");
    } else if (location.pathname === "/projects") {
      setModelName("work_vahan.glb");
    } else {
      setModelName("idle_vahan.glb");
    }
  }, [location.pathname]);

  useEffect(() => {
    const scene = new THREE.Scene();
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    const container = mountRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    const aspect = width / height;
    const frustumSize = 5;

    const camera = new THREE.OrthographicCamera(
      (-frustumSize * aspect) / 2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      1000
    );
    camera.position.set(3, 2, 6);
    camera.lookAt(0, 0, 0);

    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);

    scene.background = null;

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 2);
    scene.add(ambient);
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(3, 10, 4);
    dirLight.castShadow = true;
    scene.add(dirLight);

    const target = new THREE.Vector3(0, 0, 0);

    const blueLight = new THREE.SpotLight("#f542f2", 1000);
    blueLight.position.set(-10, 0, 10);
    blueLight.target.position.copy(target);
    blueLight.castShadow = true;
    blueLight.angle = Math.PI / 6;
    blueLight.penumbra = 0.5;
    blueLight.decay = 2;
    blueLight.distance = 2000;
    scene.add(blueLight);
    scene.add(blueLight.target);

    const redLight = new THREE.SpotLight("#0051ff", 1000);
    redLight.position.set(10, 0, 0);
    redLight.target.position.copy(target);
    redLight.castShadow = true;
    redLight.angle = Math.PI / 6;
    redLight.penumbra = 0.5;
    redLight.decay = 2;
    redLight.distance = 2000;
    scene.add(redLight);
    scene.add(redLight.target);

    function createRadialGradientTexture(size = 512) {
      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext("2d");

      const gradient = ctx.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size / 2
      );
      gradient.addColorStop(0, "rgb(209, 212, 238)");
      gradient.addColorStop(0.6, "rgba(63,113,255, 0.3)");
      gradient.addColorStop(1, "rgba(63,113,255, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    }

    const gradientTexture = createRadialGradientTexture();
    const planeGeometry = new THREE.PlaneGeometry(5, 5);
    const planeMaterial = new THREE.MeshBasicMaterial({
      map: gradientTexture,
      transparent: true,
      depthWrite: false,
    });
    const gradientPlane = new THREE.Mesh(planeGeometry, planeMaterial);
    gradientPlane.rotation.x = -Math.PI / 2;
    gradientPlane.position.set(-1.2, -1, 1);
    gradientPlane.receiveShadow = false;
    gradientPlane.castShadow = false;
    scene.add(gradientPlane);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;

    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      renderer.setSize(newWidth, newHeight);
      const newAspect = newWidth / newHeight;
      camera.left = (-frustumSize * newAspect) / 2;
      camera.right = (frustumSize * newAspect) / 2;
      camera.top = frustumSize / 2;
      camera.bottom = -frustumSize / 2;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    if (!sceneRef.current || !rendererRef.current || !cameraRef.current) return;

    const loader = new GLTFLoader();
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    const camera = cameraRef.current;

    loader.load(
      `/assets/animations/${modelName}`,
      (gltf) => {
        const oldModel = scene.getObjectByName("mainModel");

        if (oldModel) {
          const oldHead = oldModel.getObjectByName("head", true);
          if (oldHead?.userData?.cleanup) {
            oldHead.userData.cleanup();
          }
          scene.remove(oldModel);
        }

        headUpdateFunctionRef.current = null;

        const model = gltf.scene;
        model.name = "mainModel";
        model.scale.set(1, 1, 1);
        model.rotation.y = -(Math.PI / 1.4);
        model.position.set(-1.2, -1, 1);
        scene.add(model);

        modelRef.current = model;

        mixerRef.current = new THREE.AnimationMixer(model);
        mixerRef.current.timeScale = 0.5;
        gltf.animations.forEach((clip) => {
          mixerRef.current.clipAction(clip).play();
        });

        if (modelName === "idle_vahan.glb") {
          const head = model.getObjectByName("head", true);
          if (head) {
            headUpdateFunctionRef.current = enableHeadLookAt(
              head,
              camera,
              renderer
            );
            //console.log("Head tracking enabled");
          } else {
            console.warn("Head object not found in model.");
          }
        }
      },
      undefined,
      (error) => {
        console.error("Error loading model:", error);
      }
    );
  }, [modelName]);

  useEffect(() => {
    const clock = new THREE.Clock();
    const animate = () => {
      requestAnimationFrame(animate);

      if (mixerRef.current) mixerRef.current.update(clock.getDelta());

      if (headUpdateFunctionRef.current) {
        headUpdateFunctionRef.current();
      }

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();
  }, []);

  return <div ref={mountRef} className="ThreeScene" />;
};

export default ThreeScene;
